/**
 * Generate a new Idempotency-Key for a user-initiated write request.
 *
 * Pass the same value through to every retry of one intended save. Different
 * intended saves MUST use different keys — distinct user actions that happen
 * to share a payload should be treated as two creates by the server, not
 * collapsed into one. Forms that stay open between saves use
 * `useFormIdempotencyKey()`, which keeps one key per open form and request.
 *
 * Keeping this in a tiny wrapper module lets us swap the generator later
 * (e.g. to ULID for sortable audit) without hunting down every caller, and
 * makes mocking trivial in Vitest.
 */
export function newIdempotencyKey() {
    // crypto.randomUUID() requires a secure context (HTTPS or localhost).
    // Fall back to a manual UUID v4 generator when unavailable (e.g. plain
    // HTTP in local dev or test environments).
    if (typeof crypto?.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
